import React, { useEffect, useMemo } from 'react';
import { Drawer, Button, Spin } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { EAutoSaveBusinessType } from 'types/treatment';
import useVersionHistory from 'hooks/treatment/useVersionHistory';
import ExpandableText from 'components/ExpandableText';
import dayjs from 'utils/dayjs';
import s from './s.module.less';

interface VersionHistoryDrawerProps {
    open: boolean;
    onClose: () => void;
    businessId: number;
    businessType?: EAutoSaveBusinessType;
    onRestore?: (content: string) => void;
}

const VersionHistoryDrawer = ({ open, onClose, businessId, businessType, onRestore }: VersionHistoryDrawerProps) => {
    const [data, loading, fetchData] = useVersionHistory({
        businessId,
        businessType,
        autoFetch: false,
    });

    useEffect(() => {
        if (open && businessType && businessId) {
            fetchData(businessId, businessType!);
        }
    }, [open, businessType, businessId]);

    const formatTimeAgo = (timestamp: string | number) => {
        const now = dayjs();
        const time = dayjs.utc(timestamp).local();
        const diffMinutes = now.diff(time, 'minute');

        if (diffMinutes < 10) {
            return `${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`;
        } else {
            return time.format('hh:mm A');
        }
    };

    const handleRestore = (content: string) => {
        if (onRestore) {
            onRestore(content);
            onClose();
        }
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title="Version History"
            width={640}
            className={s.versionHistoryDrawer}
            zIndex={1004}
        >
            {loading ? (
                <div className={s.loadingContainer}>
                    <Spin />
                </div>
            ) : (
                <div className={s.content}>
                    <div className={s.title}>Today</div>
                    {data?.map((item, key) => (
                        <div className={s.versionItem} key={item.id}>
                            <div className={s.versionHeader}>
                                <div className={s.versionTime}>
                                    <ClockCircleOutlined className={s.clockIcon} />
                                    <span>{formatTimeAgo(item.updateAt!)}</span>
                                    {key === 0 && (
                                        <span className={s.currentBadge}>Current</span>
                                    )}
                                </div>
                                <div className={s.versionActions}>
                                    {key !== 0 && (
                                        <Button
                                            icon={
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8 14C4.93831 14 2.41225 11.7068 2.04572 8.74428L3.36888 8.57887C3.65397 10.8831 5.61869 12.6667 8 12.6667C10.5773 12.6667 12.6667 10.5773 12.6667 8C12.6667 5.42266 10.5773 3.33334 8 3.33334C6.41416 3.33334 5.01309 4.12434 4.16978 5.33334H6.66666V6.66666H2V2H3.33334V4.22844C4.43331 2.86912 6.11516 2 8 2C11.3137 2 14 4.68628 14 8C14 11.3137 11.3137 14 8 14Z" fill="#4B5563" />
                                                </svg>
                                            }
                                            onClick={() => handleRestore(item.content)}
                                            className={s.restoreBtn}
                                        >
                                            Restore
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className={s.versionContent}>
                                <ExpandableText
                                    content={item.content}
                                    moreText="Show all"
                                    lessText="Show less"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Drawer>
    );
};

export default VersionHistoryDrawer;